/** biome-ignore-all lint/correctness/useUniqueElementIds: unique login component */
/** biome-ignore-all lint/a11y/useValidAnchor: custom link */
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ButtonUI, Input, showNotification, useQueryFetch, validationErrorChecker } from 'sonata-react-components';

import { changePassword } from '@/services/authentication.service';
import { getResetPasswordSchema, type ResetPasswordFormValues } from '@/validators/user.validator';

import './styles.scss';

type State = ResetPasswordFormValues;

export default function ResetPasswordPage() {
  const { formatMessage } = useIntl();
  const resetPasswordSchema = getResetPasswordSchema(formatMessage);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const [state, setState] = useState<State>({
    password: '',
    repeatPassword: ''
  });
  const { password, repeatPassword } = state;

  useEffect(() => {
    if (!email || !token) {
      navigate('/forgot-password');
    }
  }, [email, token, navigate]);

  const { isFetching, refetch } = useQueryFetch(
    [email, token, password],
    async () => {
      await changePassword(email!, token!, password);
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Your password has been successfully changed.',
          id: 'resetPassword.successNotification'
        })
      );
      navigate('/login');
    },
    {
      enabled: false,
      onError: () => {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Error changing password',
            id: 'resetPassword.errorNotification'
          })
        );
      },
      staleTime: 0
    }
  );

  const handlePasswordChange = (newPassword: string) => {
    setState(prevState => ({ ...prevState, password: newPassword }));
  };

  const handleRepeatPasswordChange = (newRepeatPassword: string) => {
    setState(prevState => ({ ...prevState, repeatPassword: newRepeatPassword }));
  };

  const handleChangePasswordClick = () => {
    refetch();
  };

  const handleCancelClick = () => {
    navigate('/login');
  };

  const errors = validationErrorChecker(state, resetPasswordSchema);

  return (
    <div className="resetPassword">
      <h1 className="resetPassword__title">
        {formatMessage({
          defaultMessage: 'Reset your password',
          id: 'resetPassword.title'
        })}
      </h1>

      <form className="resetPassword__form">
        <span className="resetPassword__passwordContainer">
          <span>
            {formatMessage({
              defaultMessage: 'Confirm the change by entering your new password.',
              id: 'resetPassword.resetInstructions'
            })}
          </span>
          <Input
            id="password"
            label={formatMessage({
              defaultMessage: 'New Password',
              id: 'resetPassword.passwordLabel'
            })}
            name="password"
            onChange={textInput => handlePasswordChange(textInput as string)}
            placeholder={formatMessage({
              defaultMessage: 'Enter your new password',
              id: 'resetPassword.passwordPlaceholder'
            })}
            shape="squareUI"
            type="password"
            value={password}
          />
          <Input
            id="confirmPassword"
            label={formatMessage({
              defaultMessage: 'Confirm new password',
              id: 'resetPassword.confirmPasswordLabel'
            })}
            name="confirmPassword"
            onChange={textInput => handleRepeatPasswordChange(textInput as string)}
            placeholder={formatMessage({
              defaultMessage: 'Enter your new password',
              id: 'resetPassword.confirmPasswordPlaceholder'
            })}
            shape="squareUI"
            type="password"
            value={repeatPassword}
          />
        </span>
        <div className="resetPassword__actions">
          <ButtonUI
            className="resetPassword__cancelButton"
            id="resetPassword-cancelButton"
            onClick={handleCancelClick}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Cancel',
              id: 'common.cancel'
            })}
            type="secondary"
          />
          <ButtonUI
            className="resetPassword__sendButton"
            disabled={Object.keys(errors || {}).length > 0}
            id="resetPassword-sendButton"
            loading={isFetching}
            onClick={handleChangePasswordClick}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Change password',
              id: 'resetPassword.changePasswordButton'
            })}
            type="primary"
          />
        </div>
      </form>
    </div>
  );
}
